import { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import Decimal from "decimal.js-light";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  Grid,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { fractionUnitValues } from "dora-contracts";
import { useAppDispatch, useSelector } from "../../../redux-store";
import {
  FormData,
  close,
  selectFractionDialogOpen,
  selectFractionDialogData,
} from "../../../ducks/app/fraction-dialog";
import * as dataActions from "../../../ducks/data/fractions";
import { loadProducts } from "../../../ducks/data/products";
import { selectProducts } from "../../../ducks/data/products/selectors";
import { UFDoraSelect, UFDoraDecimalInput } from "dora-shared/frontend";
import emptyToNull from "./empty-to-null";

// The dialog state (and the API) carry the price as an invariant decimal
// string; the form holds it as a Decimal, so nothing in between parses it.
type FormValues = Omit<FormData, "salesPrice"> & { salesPrice: Decimal | null };

const OpenFractionDialog = ({ initialData }: { initialData: FormData }) => {
  const { t } = useTranslation("equipment");
  const dispatch = useAppDispatch();
  const products = useSelector(selectProducts);
  useEffect(() => {
    dispatch(loadProducts());
  }, [dispatch]);

  const schema = useMemo(
    () =>
      yup.object({
        name: yup.string().required(t("fractions.fields.nameRequired")),
        code: yup.string().nullable(),
        color: yup.string().nullable(),
        unit: yup.string().required(),
        eakCode: yup.string().nullable(),
        productId: yup.string().nullable(),
        salesPrice: yup
          .mixed<Decimal>()
          .nullable()
          .test(
            "non-negative",
            t("fractions.fields.salesPriceInvalid"),
            (v) => v == null || v.gte(0)
          ),
      }),
    [t]
  );

  const {
    register,
    handleSubmit,
    control,
    formState: { isSubmitting, errors },
  } = useForm<FormValues>({
    resolver: yupResolver(schema),
    defaultValues: {
      ...initialData,
      salesPrice: initialData.salesPrice
        ? new Decimal(initialData.salesPrice)
        : null,
    },
  });

  const unitOptions = useMemo(
    () => fractionUnitValues.map((u) => ({ value: u, label: u })),
    []
  );
  const productOptions = useMemo(
    () => [
      { value: null, label: t("fractions.fields.noProduct") },
      ...products.map((p) => ({
        value: p.id,
        label: `${p.productNumber} – ${p.name}`,
      })),
    ],
    [products, t]
  );

  const onSubmit = async (v: FormValues) => {
    const payload = {
      name: v.name,
      code: emptyToNull(v.code),
      color: emptyToNull(v.color),
      unit: v.unit,
      eakCode: emptyToNull(v.eakCode),
      productId: emptyToNull(v.productId),
      salesPrice: v.salesPrice === null ? null : v.salesPrice.toString(),
    };
    if (initialData.id) {
      await dispatch(
        dataActions.updateFraction({ ...payload, id: initialData.id })
      );
    } else {
      await dispatch(dataActions.addFraction(payload));
    }
  };

  return (
    <Dialog open onClose={() => dispatch(close())} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogTitle>
          {initialData.id ? t("fractions.editTitle") : t("fractions.addTitle")}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <TextField
              label={t("fractions.fields.name")}
              required
              {...register("name")}
              error={!!errors.name}
              helperText={errors.name?.message}
              fullWidth
            />
            <Grid container spacing={2}>
              <Grid size={4}>
                <TextField
                  label={t("fractions.fields.code")}
                  {...register("code")}
                  fullWidth
                />
              </Grid>
              <Grid size={4}>
                <UFDoraSelect
                  name="unit"
                  control={control}
                  label={t("fractions.fields.unit")}
                  options={unitOptions}
                  fullWidth
                />
              </Grid>
              <Grid size={4}>
                <TextField
                  label={t("fractions.fields.eakCode")}
                  {...register("eakCode")}
                  fullWidth
                />
              </Grid>
            </Grid>
            <div>
              <UFDoraSelect
                name="productId"
                control={control}
                label={t("fractions.fields.product")}
                options={productOptions}
                nullable
                fullWidth
              />
            </div>
            <UFDoraDecimalInput
              name="salesPrice"
              control={control}
              label={t("fractions.fields.salesPrice")}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => dispatch(close())} color="inherit">
            {t("actions.cancel")}
          </Button>
          <Button type="submit" variant="contained" disabled={isSubmitting}>
            {t("actions.save")}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

const FractionDialog = () => {
  const open = useSelector(selectFractionDialogOpen);
  const initialData = useSelector(selectFractionDialogData);
  if (!open || !initialData) return null;
  return <OpenFractionDialog initialData={initialData} />;
};

export default FractionDialog;
